import type { DeviceModel } from '@octave/contracts';
import { useMemo, useState } from 'react';

export interface CatalogFilters {
  brand?: string;
  capacity?: string;
  maxPrice: number;
  searchQuery: string;
}

const defaultFilters: CatalogFilters = {
  brand: '',
  capacity: '',
  maxPrice: 0, // Sera initialisé avec le prix max des données
  searchQuery: '',
};

export function useCatalogFilters(deviceModels: DeviceModel[] = []) {
  const [filters, setFilters] = useState<CatalogFilters>(() => {
    // Initialiser le prix max avec la valeur maximale des données
    const maxPriceFromData =
      deviceModels.length > 0
        ? Math.max(
            ...deviceModels
              .flatMap((model) => model.variants)
              .map((variant) => variant.price)
          )
        : 0;

    return {
      ...defaultFilters,
      maxPrice: maxPriceFromData,
    };
  });

  // Filtrer les modèles selon les critères
  const filteredModels = useMemo(() => {
    if (!deviceModels || deviceModels.length === 0) return [];

    return deviceModels.filter((model) => {
      // Filtre par marque
      if (
        filters.brand &&
        filters.brand !== '' &&
        model.brand !== filters.brand
      ) {
        return false;
      }

      // Filtre par capacité (si au moins une variante a cette capacité)
      if (filters.capacity && filters.capacity !== '') {
        const hasCapacity = model.variants?.some(
          (variant) =>
            variant.capacity && variant.capacity.toString() === filters.capacity
        );
        if (!hasCapacity) return false;
      }

      // Filtre par prix maximum (si au moins une variante est dans la fourchette)
      if (filters.maxPrice > 0) {
        const hasAffordableVariant = model.variants?.some(
          (variant) => variant.price <= filters.maxPrice
        );
        if (!hasAffordableVariant) return false;
      }

      // Filtre par recherche textuelle
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const matchesSearch =
          model.name.toLowerCase().includes(query) ||
          model.brand.toLowerCase().includes(query) ||
          (model.description &&
            model.description.toLowerCase().includes(query));

        if (!matchesSearch) return false;
      }

      // S'assurer que le modèle a des variantes
      return model.variants && model.variants.length > 0;
    });
  }, [deviceModels, filters]);

  const updateFilters = (newFilters: Partial<CatalogFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const clearFilters = () => {
    const maxPriceFromData =
      deviceModels.length > 0
        ? Math.max(
            ...deviceModels
              .flatMap((model) => model.variants)
              .map((variant) => variant.price)
          )
        : 0;

    setFilters({
      ...defaultFilters,
      maxPrice: maxPriceFromData,
    });
  };

  return {
    filters,
    filteredModels,
    updateFilters,
    clearFilters,
  };
}
